import { Component, DOCUMENT, ElementRef, EventEmitter, HostListener, Inject, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';

import { AGUAppModuleDefinition, AGUNavTone } from '../../app-surface/app-module-definition';
import { ToneOf } from '../../app-surface/nav-tone';
import { AGUSidebarIdentity } from '../app-sidebar/app-sidebar.component';
import { SidebarNavLabel } from '../app-sidebar/sidebar-nav-label';
import { ModalFocusController } from './modal-focus';

/**
 * The phone drawer (`.mdrawer` over `.mscrim`, P/Member_Hub.html:517-526, 882-892): a right-hand sheet the app bar's avatar opens,
 * cloning the sidebar's identity block and module rail (P/Member_Hub.html:887-889), then the account actions. The Science Talent Network
 * module is NOT cloned: the prototype drops `.side-stn` on phones (:530).
 *
 * A modal dialog: `role="dialog"` + `aria-modal`, focus moves to the close button on open and is trapped inside (a document-level
 * Tab listener held only while open, so focus that has strayed outside is pulled back in), Escape closes,
 * `body` and the surface's scrollport (the host's nearest scrolling ancestor) are locked while open, and focus returns to the opener
 * on close — all through `ModalFocusController`, which the filter sheet shares. The open state is the host's (`Open` in, `Closed` out).
 *
 * A new ARIA implementation rather than a wrapped `mj-left-nav`: its off-canvas drawer cannot be hosted in the RA token layer or
 * restyled through a theming API (docs/mj-upstream-defects.md MJ-G10).
 */
@Component({
    standalone: false,
    selector: 'agu-drawer',
    templateUrl: './drawer.component.html',
    styleUrl: './drawer.component.scss',
})
export class AGUDrawerComponent implements OnChanges, OnDestroy {
    @Input() Open = false;
    @Input() Identity: AGUSidebarIdentity | null = null;
    @Input() Modules: readonly AGUAppModuleDefinition[] = [];
    @Input() ActiveModuleKey = '';
    /** Whether the host can take the member back to the Explorer — the sidebar account menu's rule. */
    @Input() CanReturnToHost = false;

    @Output() Closed = new EventEmitter<void>();
    @Output() ModuleSelected = new EventEmitter<AGUAppModuleDefinition>();
    @Output() ProfileRequested = new EventEmitter<void>();
    @Output() ReturnToHostRequested = new EventEmitter<void>();
    @Output() SignOutRequested = new EventEmitter<void>();

    /** The dialog element; public so a spec can supply it without a TestBed. */
    @ViewChild('panel') Panel?: ElementRef<HTMLElement>;
    @ViewChild('closeButton') private closeButton?: ElementRef<HTMLButtonElement>;

    /** Focus trap, scroll lock (body and the surface scrollport) and focus return, shared with the filter sheet. */
    private readonly modal: ModalFocusController;

    constructor(
        @Inject(DOCUMENT) document: Document,
        @Inject(ElementRef) private readonly host: ElementRef<HTMLElement> | null = null
    ) {
        this.modal = new ModalFocusController(document, () => this.Panel?.nativeElement ?? null, () => this.closeButton?.nativeElement ?? null);
    }

    get VisibleModules(): readonly AGUAppModuleDefinition[] {
        return this.Modules.filter((m) => !m.HiddenFromNav);
    }
    IsActive(module: AGUAppModuleDefinition): boolean { return module.Key === this.ActiveModuleKey; }
    ToneOf(module: AGUAppModuleDefinition): AGUNavTone { return ToneOf(module); }
    LabelOf(module: AGUAppModuleDefinition): string { return SidebarNavLabel(module); }

    ngOnChanges(changes: SimpleChanges): void {
        const open = changes['Open'];
        if (!open || open.currentValue === open.previousValue) return;
        if (this.Open) this.Activate();
        else this.Deactivate();
    }

    ngOnDestroy(): void {
        this.Deactivate();
    }

    Close(): void { this.Closed.emit(); }
    Select(module: AGUAppModuleDefinition): void { this.ModuleSelected.emit(module); }
    RequestProfile(): void { this.ProfileRequested.emit(); }

    /** Escape closes (P/Member_Hub.html:1020). */
    @HostListener('document:keydown.escape') OnEscape(): void {
        if (this.Open) this.Close();
    }

    /**
     * Tab and Shift+Tab stay inside the open drawer: they wrap at either end, focus outside the panel is moved into it, and a panel with
     * nothing focusable holds focus on itself rather than letting Tab escape.
     */
    OnKeydown(event: KeyboardEvent): void {
        if (!this.Open) return;
        this.modal.OnKeydown(event);
    }

    private Activate(): void {
        this.modal.Activate(this.host?.nativeElement ?? null);
    }

    private Deactivate(): void {
        this.modal.Deactivate();
    }
}
